const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MIDNIGHT_UTC_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T00:00:00(?:\.\d+)?(?:Z|\+00:00)$/i;
const US_DATE_PATTERN = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/;

const localCalendarDate = (match: RegExpMatchArray): Date =>
  new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));

const localUsDate = (match: RegExpMatchArray): Date => {
  const year = match[3].length === 2 ? 2000 + Number(match[3]) : Number(match[3]);
  return new Date(year, Number(match[1]) - 1, Number(match[2]));
};

/**
 * Parses values for display while preserving date-only business values.
 *
 * PostgreSQL/.NET may serialize a date such as 2026-05-20 as midnight UTC.
 * Treating that value as an instant moves it to the prior day in timezones west
 * of UTC, so date-only and midnight-UTC inputs are constructed as local calendar
 * dates. Other timestamps retain their normal instant/local-time behavior.
 */
export const parseDisplayDate = (value: unknown): Date | null => {
  if (value == null || value === '') return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim();
    if (!normalizedValue) return null;

    const calendarDateMatch = normalizedValue.match(DATE_ONLY_PATTERN)
      ?? normalizedValue.match(MIDNIGHT_UTC_PATTERN);
    if (calendarDateMatch) return localCalendarDate(calendarDateMatch);

    const usDateMatch = normalizedValue.match(US_DATE_PATTERN);
    if (usDateMatch) return localUsDate(usDateMatch);

    const parsedDate = new Date(normalizedValue);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  if (typeof value === 'number') {
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  return null;
};
